// Contrail V3.9.0 — "Find a location": paste coordinates (any common format) or a Google Maps link, and the map flies
// there and opens the location card (where it can be saved as a place). Short links (maps.app.goo.gl) are followed by
// /api/resolve-link on the server.
import { FormEvent, useState } from "react";
import { supabase } from "../../lib/supabase";
import { isGoogleMapsLink, isShortMapsLink, parseCoords } from "../../lib/coords";
import { ToolPanel } from "./MapTools";

const RECENT_KEY = "fw.find.recent";
const readRecent = (): { q: string; lat: number; lng: number }[] => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]"); } catch { return []; } };

export default function FindTool({ onFound, onClose }: { onFound: (lat: number, lng: number, title: string) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [recent, setRecent] = useState(readRecent);

  function done(lat: number, lng: number, text: string) {
    const r = [{ q: text.slice(0, 200), lat, lng }, ...recent.filter((x) => x.q !== text)].slice(0, 5);
    setRecent(r); try { localStorage.setItem(RECENT_KEY, JSON.stringify(r)); } catch { /* ignore */ }
    onFound(lat, lng, "Found location");
  }

  async function go(e?: FormEvent) {
    e?.preventDefault();
    const text = q.trim();
    if (!text) return;
    setErr(null);
    const direct = parseCoords(text);
    if (direct) return done(direct.lat, direct.lng, text);
    if (isShortMapsLink(text) || isGoogleMapsLink(text)) {
      setBusy(true);
      try {
        const { data } = await supabase.auth.getSession();
        const r = await fetch(`/api/resolve-link?url=${encodeURIComponent(text)}`, { headers: { Authorization: `Bearer ${data.session?.access_token ?? ""}` } });
        const j = await r.json().catch(() => ({}));
        if (r.ok && typeof j.lat === "number") return done(j.lat, j.lng, text);
        setErr(j.error ?? "No coordinates in that link.");
      } catch { setErr("Couldn't open that link right now."); }
      finally { setBusy(false); }
      return;
    }
    setErr("Not recognised. Try “-17.7553, 177.4431”, “17°45'19\"S 177°26'35\"E” or a Google Maps link.");
  }

  return (
    <ToolPanel title="Find a location" onClose={onClose}>
      <form className="find-form" onSubmit={go}>
        <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setErr(null); }} placeholder="Coordinates or Google Maps link"
          aria-label="Coordinates or Google Maps link" spellCheck={false} />
        <button className="btn-primary small" disabled={busy || !q.trim()}>{busy ? "…" : "Go"}</button>
      </form>
      {err && <div className="error">{err}</div>}
      <p className="sub">Works with decimal (-17.7553, 177.4431), degrees-minutes-seconds (17°45'19"S 177°26'35"E), N/S/E/W letters,
        and Google Maps links, including short share links (maps.app.goo.gl).</p>
      {recent.length > 0 && <div className="find-recent">
        <h4>Recent</h4>
        {recent.map((r) => (
          <button key={r.q} type="button" className="find-r" onClick={() => onFound(r.lat, r.lng, "Found location")} title={r.q}>
            <span className="mono">{r.lat.toFixed(5)}, {r.lng.toFixed(5)}</span><span className="sub">{r.q.length > 42 ? r.q.slice(0, 42) + "…" : r.q}</span>
          </button>
        ))}
      </div>}
    </ToolPanel>
  );
}
